import { useModal } from "@npco/zeller-design-system";
import { useForm, bindField } from "@npco/zeller-design-system/form";
import * as Icon from "@npco/zeller-design-system/icon";
import * as Input from "@npco/zeller-design-system/input";
import { ADT } from "pb.adt";
import { useState, useEffect, useCallback, useMemo } from "react";
import { z } from "zod";

import { Layout } from "../shared/layout";
import { useApiClient } from "../shared/use-api-client";
import type { Page } from "../terminal-setup";
import { getBadgeProperties } from "./page-terminal/get-badge-properties";
import {
	getSourceOverrideState,
	type OverrideState,
} from "./page-terminal/get-source-override-state";
import { getTerminals } from "./page-terminal/get-terminals";

const ValuesSchema = z.object({
	terminalId: z.string().nonempty(),
});

export function PageTerminal({
	page: { deviceId, terminalId, changePairing, onNext, onBack },
	onClose,
	onLogout,
	pending,
}: {
	page: ADT.Pick<Page, "Terminal">;
} & Required<Pick<Layout.Props, "onClose" | "onLogout" | "pending">>) {
	const entities = useInputTilesDevicesPreset({ deviceId, terminalId });

	const [cannotPairOverride, setCannotPairOverride] = useState<
		"OVERRIDE_AT_POS" | "OVERRIDE_AT_SITE"
	>();
	const cannotPairModal = useModal({
		variant: "decision",
		title: changePairing ? "Cannot override current pairing" : "Cannot pair",
		content: cannotPairOverride
			? getOverrideStateDescription(cannotPairOverride)
			: undefined,
		secondary: false,
		primary: { label: "Close" },
		overlayBlur: true,
	});

	const [overrideTarget, setOverrideTarget] = useState<{
		terminalId: string;
		label: string;
	}>();
	const overridePairingModal = useModal({
		variant: "decision",
		title: "Override Pairing",
		content: (
			<>
				Are you sure you want to pair the Zeller Terminal{" "}
				<strong>{overrideTarget?.label}</strong> to this device? This will
				unpair you from <strong>{changePairing?.currentTerminalName}</strong>.
			</>
		),
		primary: {
			label: "Confirm",
			onClick: async () => {
				if (overrideTarget) {
					await onNext(overrideTarget.terminalId);
				}
			},
		},
		overlayBlur: true,
	});

	const form = useForm(ValuesSchema, async ({ value: { terminalId } }) => {
		const nextTerminal = (() => {
			/* v8 ignore next 3 -- defensive: Next is disabled until options load, so this submit callback never runs without options */
			if (!entities.options) {
				return undefined;
			}

			for (const group of entities.options) {
				for (const option of group.options) {
					if (option.id === terminalId) {
						return option;
					}
				}
			}

			/* v8 ignore next -- defensive: the selected id always exists in the rendered options */
			return undefined;
		})();

		/* v8 ignore next 5 -- defensive: a resolved option always carries a label, so this guard only fires on inconsistent internal state */
		if (!nextTerminal?.label) {
			throw new Error(
				"Failed to resolve selected terminal name from terminal id.",
			);
		}

		const metadata = entities.optionsMetadata.get(nextTerminal.id);

		if (
			metadata?.override === "OVERRIDE_AT_POS" ||
			metadata?.override === "OVERRIDE_AT_SITE"
		) {
			setCannotPairOverride(metadata.override);
			return cannotPairModal.open();
		}

		if (!changePairing) {
			return onNext(terminalId);
		}

		setOverrideTarget({ terminalId, label: nextTerminal.label });
		return overridePairingModal.open();
	});

	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Layout
					title="Choose Terminal"
					progress
					progressValue={3}
					progressLimit={4}
					pending={pending || entities.pending || (isSubmitting && "submit")}
					onClose={onClose}
					onLogout={onLogout}
					actions={[
						{
							label: "Next",
							onClick: form.handleSubmit,
							primary: true,
							disabled: !entities.options,
						},
						onBack && { label: "Back", onClick: onBack },
					]}
				>
					{cannotPairModal.element}
					{overridePairingModal.element}
					<div className="px-32 pt-6">
						<form.Field
							name="terminalId"
							children={(field) => (
								<Input.Tiles
									{...bindField(field)}
									{...entities}
									label="Find your device"
								/>
							)}
						/>
					</div>
				</Layout>
			)}
		</form.Subscribe>
	);
}

// ============================================================================

type OptionsMetadata = Map<string, { override: OverrideState }>;

const terminalIcon = <Icon.Terminal />;

function useInputTilesDevicesPreset({
	deviceId,
	terminalId,
}: {
	deviceId: string;
	terminalId?: string;
}) {
	const [optionsData, setOptionsData] = useState<
		ReadonlyArray<Input.Tiles.Group> | Error
	>();
	const optionsMetadata = useMemo<OptionsMetadata>(() => new Map(), []);

	const apiClient = useApiClient();

	const retry = useCallback(async () => {
		setOptionsData(undefined);
		optionsMetadata.clear();

		const terminals = await getTerminals({ apiClient, deviceId });
		if (terminals instanceof Error) {
			return setOptionsData(terminals);
		}

		const { sites } = terminals;

		return setOptionsData(
			sites.getSites.sites.reduce((sites, site) => {
				if (site) {
					sites.push({
						id: site.id,
						label: site.name,
						options:
							site.devices?.map(({ id: optionId, name, posSettings }) => {
								const isPaired = optionId === terminalId;
								const source = posSettings?.active ?? undefined;
								const sourceName = posSettings?.posRegisterName ?? undefined;

								const badge = getBadgeProperties({
									isPaired,
									source,
									sourceName,
								});

								const override = getSourceOverrideState(source);
								optionsMetadata.set(optionId, { override });

								return {
									id: optionId,
									disabled: isPaired,
									label: name ?? "Unknown",
									icon: terminalIcon,
									badge,
								};
							}) ?? [],
					});
				}
				return sites;
			}, [] as Array<Input.Tiles.Group>),
		);
	}, [apiClient, deviceId, optionsMetadata, terminalId]);

	useEffect(() => {
		void retry();
	}, [retry]);

	// const [searchValue, setSearchValue] = useState<string>();
	const options = useMemo(() => {
		if (optionsData instanceof Error) {
			return undefined;
		}

		return optionsData;

		// if (!searchValue) {
		//   return optionsData;
		// }

		// const searchValueCompare = searchValue.toLowerCase();
		// return optionsData?.filter((option) => {
		//   const optionLabelCompare = option.label.toLowerCase();
		//   return optionLabelCompare.includes(searchValueCompare);
		// });
		// }, [optionsData, searchValue]);
	}, [optionsData]);

	return {
		pending: optionsData === undefined,
		options,
		optionsError: optionsData instanceof Error ? "network" : undefined,
		optionsMetadata,
		optionsOnRetry: retry,
		groups: true,
		// search: true,
		// searchValue,
		// searchOnChange: setSearchValue,
	} satisfies Partial<Input.Tiles.Props> & {
		optionsMetadata: OptionsMetadata;
	};
}

export function getOverrideStateDescription(
	overrideState: "OVERRIDE_AT_POS" | "OVERRIDE_AT_SITE",
) {
	if (overrideState === "OVERRIDE_AT_POS") {
		return "Pairing for this Terminal is managed at the POS device level. To continue, unpair from your POS device.";
	}
	return "Pairing for this Terminal is managed at the site level. To continue, re-assign this Terminal to another site.";
}
